import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environments';
import { firstValueFrom } from 'rxjs';
import {
  DevelopmentAreaPayload,
  CorridorSystem,
  CorridorSystemPayload,
  CorridorSystemFilterPayload,
  CorridorSystemFilterColumn,
  CorridorSystemFilterOptions,
  SaveCorridorSystemPayload,
  DeleteCorridorSystemPayload,
} from './interfaces/fe-corridor-systems.interfaces';
import { ErrorHandler } from '../utils/error-handler';

@Injectable({
  providedIn: 'root',
})
export class FeCorridorSystemsService {
  private baseUrl = environment.WebApiURL;
  private readonly feCorridorExportTabName = 'FECorridorSystem';

  private feCorridorDropDownUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/FECorridorDropDown`;
  }

  private feCorridorSystemsUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/GetFECorridorDetails`;
  }

  private feCorridorMultiFilterUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/FECorridorMultiFilter`;
  }

  private saveFECorridorSystemsUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/InsertUpdateFECorridorSystem`;
  }

  private deleteCorridorUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/DeleteFECorridorDetails`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  constructor(private http: HttpClient) {}

  async getDevelopmentAreas(): Promise<string[]> {
    try {
      const payload: DevelopmentAreaPayload = {
        developmentArea: null,
        cs: null,
      };

      const response = await firstValueFrom(
        this.http.post<any[]>(this.feCorridorDropDownUrl(), payload)
      );

      const devAreaItem = response.find((item: any) => item.columnName === 'DevelopmentArea');

      if (!devAreaItem) {
        return [];
      }

      const developmentAreas = devAreaItem.values || [];
      return developmentAreas;
    } catch (error) {
      return [];
    }
  }

  async getFacilitiesByDevArea(devArea: string): Promise<string[]> {
    try {
      const payload: DevelopmentAreaPayload = {
        developmentArea: devArea,
        cs: null,
      };

      const response = await firstValueFrom(
        this.http.post<any[]>(this.feCorridorDropDownUrl(), payload)
      );

      const csItem = response.find((item: any) => item.columnName === 'CS');
      return csItem?.values || [];
    } catch (error) {
      return [];
    }
  }

  private buildFilterPayload(filters?: Partial<CorridorSystemFilterPayload>): CorridorSystemFilterPayload {
    return {
      developmentArea: filters?.developmentArea || null,
      facilityType: filters?.facilityType || null,
      facilityNames: filters?.facilityNames || null,
      pseudoFacilityName: filters?.pseudoFacilityName || null,
    };
  }

  private parseFilterResponse(
    response: CorridorSystemFilterColumn[]
  ): CorridorSystemFilterOptions {
    const filterOptions: CorridorSystemFilterOptions = {
      developmentArea: [],
      facilityType: [],
      facilitiesContained: [],
      pseudoFacilityName: [],
    };

    response.forEach((column) => {
      switch (column.columnName) {
        case 'DevelopmentArea':
          filterOptions.developmentArea = column.values || [];
          break;
        case 'FacilityType':
          filterOptions.facilityType = column.values || [];
          break;
        case 'FacilityNames':
          filterOptions.facilitiesContained = column.values || [];
          break;
        case 'PseudoFacilityName':
          filterOptions.pseudoFacilityName = column.values || [];
          break;
      }
    });

    return filterOptions;
  }

  async getFilterOptionsBySelection(
    filters?: Partial<CorridorSystemFilterPayload>
  ): Promise<CorridorSystemFilterOptions> {
    try {
      const requestBody = this.buildFilterPayload(filters);
      const response = await firstValueFrom(
        this.http.post<CorridorSystemFilterColumn[]>(
          this.feCorridorMultiFilterUrl(),
          requestBody
        )
      );

      return this.parseFilterResponse(response || []);
    } catch (error) {
      ErrorHandler.handleError(
        error,
        'FeCorridorSystemsService.getFilterOptionsBySelection'
      );
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getCorridorSystems(
    filters?: Partial<CorridorSystemFilterPayload>
  ): Promise<CorridorSystem[]> {
    try {
      const payload: CorridorSystemPayload = {
        ...this.buildFilterPayload(filters),
        lastRowNumber: 0,
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.feCorridorSystemsUrl(), payload)
      );

      const corridorSystems = response.feCorridorNames || [];

      return corridorSystems.map((item: any) => ({
        developmentArea: item.developmentArea || '',
        facilityType: item.facilityType || '',
        facilitiesContained: this.parseFacilitiesContained(
          item.facilityNames || item.facilitiesContained || []
        ),
        pseudoFacilityName: item.pseudoFacilityName || '',
        isEditing: false,
        isNew: false,
      }));
    } catch (error) {
      return [];
    }
  }

  async exportAllCorridorSystemsData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.feCorridorExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `fe-corridor-systems${inferredExtension}`;
    const rawFileName = rawHeaderFileName || fallbackFileName;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return {
      blob,
      fileName,
    };
  }

  private parseFacilitiesContained(facilities: any): string[] {
    if (Array.isArray(facilities)) {
      return facilities;
    }
    if (typeof facilities === 'string') {
      return facilities
        .split(',')
        .map((f) => f.trim())
        .filter((f) => f.length > 0);
    }
    return [];
  }

  private parseJsonResponse(response: any): any {
    let parsed = response;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { statusMessage: parsed };
      }
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      parsed = parsed[0];
    }
    return parsed;
  }

  async saveCorridorSystem(
    corridorSystem: CorridorSystem,
    action: 'INSERT' | 'UPDATE',
    userName: string,
    oldPseudoFacilityName?: string
  ): Promise<any> {
    try {
      const pseudoFacilityName = (corridorSystem.pseudoFacilityName || '').trim();
      const previousPseudoFacilityName =
        oldPseudoFacilityName === undefined
          ? null
          : (oldPseudoFacilityName || '').trim();
      const hasPseudoFacilityNameChanged =
        action === 'INSERT' || pseudoFacilityName !== (previousPseudoFacilityName || '');

      const body: SaveCorridorSystemPayload = {
        Action: action,
        DevelopmentArea: corridorSystem.developmentArea,
        FacilityType: corridorSystem.facilityType,
        FacilityNames: corridorSystem.facilitiesContained.join(','),
        PseudoFacilityName: hasPseudoFacilityNameChanged
          ? pseudoFacilityName || null
          : previousPseudoFacilityName,
        OldPseudoFacilityName: previousPseudoFacilityName,
        UserName: userName,
      };

      const response = await firstValueFrom(
        this.http.post(this.saveFECorridorSystemsUrl(), body, {
          responseType: 'text' as 'json',
        })
      );

      const parsed = this.parseJsonResponse(response);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode,
        statusMessage: result.message,
        error: !result.success,
        ...parsed,
      };
    } catch (error: any) {
      const rawError = error?.error ?? error;
      const parsed = this.parseJsonResponse(rawError);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode || error?.status || 500,
        statusMessage: result.message,
        error: true,
      };
    }
  }

  async deleteCorridorSystem(
    pseudoFacilityName: string,
    userName: string
  ): Promise<any> {
    const name = (pseudoFacilityName || '').trim();
    const user = (userName || '').trim();

    if (!name) {
      throw new Error('pseudoFacilityName is required.');
    }
    if (!user) {
      throw new Error('userName is required.');
    }

    const body: DeleteCorridorSystemPayload = {
      pseudoFacilityNames: name,
      userName: user,
    };

    try {
      const response = await firstValueFrom(
        this.http.post(this.deleteCorridorUrl(), body, {
          responseType: 'text' as 'json',
        })
      );

      const parsed = this.parseJsonResponse(response);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode,
        statusMessage: result.message,
        error: !result.success,
        ...parsed,
      };
    } catch (error: any) {
      const rawError = error?.error ?? error;
      const parsed = this.parseJsonResponse(rawError);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode || error?.status || 500,
        statusMessage: result.message,
        error: true,
      };
    }
  }
}
export type { CorridorSystem } from './interfaces/fe-corridor-systems.interfaces';